import {sendConstructionValidationMessage, setInvalidPendingBuildings} from '../../../redux/actions';
import {
  selectInvalidPendingBuildings,
  selectPendingBuildings,
} from '../../../redux/reducers/sharedStateReducer/selectors';
import {TOKEN_NAME_MAPPING} from '../../../constants/tokens';
import {getTokenConfig} from '../../buildings/getTokenConfig';
import {checkIfSpecialBuilding} from '../../buildings/checkIfSpecialBuilding';
import {applyConstructionUndoPlan, checkConstructionUndoPlanOnCopy} from './applyConstructionUndoPlan';
import {createUndoConstructionAction} from './createUndoConstructionAction';
import {
  buildInvalidPendingBuildingsMessage,
  INVALID_PENDING_BUILDINGS_HEADERS,
  validateCurrentPlayerPendingBuildings,
} from './pendingBuildingsValidation';
import {
  CONSTRUCTION_ROLLBACK_FALLBACK_REASONS,
  CONSTRUCTION_ROLLBACK_REASONS,
  planConstructionRollback,
} from './planConstructionRollback';
import {checkIfRepairRecord} from './rewindConstructionState';

// Причины запасного пути, которых планировщик не знает: план есть, но
// разошелся с редьюсерами либо расчет упал. Это ошибки кода, а не данных хода
const APPLY_FALLBACK_REASONS = {
  COPY_MISMATCH: 'copy_mismatch',
  APPLY_MISMATCH: 'apply_mismatch',
  STILL_INVALID: 'still_invalid',
  EXCEPTION: 'exception',
};

// Тост сообщения не прокручивается: длинные перечни обрезаются
const MESSAGE_GROUP_MAX_LINES = 4;

const RECORD_KINDS = {
  CONSTRUCTION: 'construction',
  SPECIAL: 'special',
  REPAIR: 'repair',
};

const getRecordKind = (building) => {
  if (checkIfRepairRecord(building)) {
    return RECORD_KINDS.REPAIR;
  }
  return checkIfSpecialBuilding(building.token) ? RECORD_KINDS.SPECIAL : RECORD_KINDS.CONSTRUCTION;
};

const getRecordLabel = (building) => (checkIfRepairRecord(building)
  ? `Ремонт: ${building.tokenLabel || 'строение'}`
  : getTokenConfig(building.token)?.label || TOKEN_NAME_MAPPING[building.token] || 'Строение');

const getRecordKey = (building) => `${building.type || ''}|${building.hexKey}|${building.token || ''}`;

// Индексы исходных действий хода, которых в сторе больше нет. Сверка по факту,
// а не по плану: после сбоя на полпути и отката с конца план уже не описывает
// того, что снято
const findUndoneIndexes = (originalRecords, currentRecords) => {
  const remainingByKey = new Map();
  currentRecords.forEach((building) => {
    const key = getRecordKey(building);
    remainingByKey.set(key, (remainingByKey.get(key) || 0) + 1);
  });
  const undoneIndexes = [];
  originalRecords.forEach((building, index) => {
    const key = getRecordKey(building);
    const remaining = remainingByKey.get(key) || 0;
    if (remaining > 0) {
      remainingByKey.set(key, remaining - 1);
    } else {
      undoneIndexes.push(index);
    }
  });
  return undoneIndexes;
};

// Одинаковые строки склеиваются («Дорога ×2»), перечень обрезается
const formatMessageLines = (lines) => {
  const counts = new Map();
  lines.forEach((line) => counts.set(line, (counts.get(line) || 0) + 1));
  const merged = [...counts].map(([line, count]) => (count > 1 ? `${line} ×${count}` : line));
  const visible = merged.slice(0, MESSAGE_GROUP_MAX_LINES).map((line) => `\n• ${line}`);
  if (merged.length > MESSAGE_GROUP_MAX_LINES) {
    visible.push(`\n• и еще ${merged.length - MESSAGE_GROUP_MAX_LINES}`);
  }
  return visible.join('');
};

const buildSelectiveMessage = (items, keptCount) => {
  const getItemsWithReason = (reason) => items.filter((item) => item.reason === reason);
  const invalidItems = getItemsWithReason(CONSTRUCTION_ROLLBACK_REASONS.INVALID);
  const dependentItems = getItemsWithReason(CONSTRUCTION_ROLLBACK_REASONS.DEPENDENT);
  const tailItems = getItemsWithReason(CONSTRUCTION_ROLLBACK_REASONS.TAIL);

  let message = 'Время хода вышло — часть строительства отменена.';
  if (invalidItems.length > 0) {
    // Тексты ошибок проверки уже называют строение
    message += `\n\nРазмещено некорректно:${formatMessageLines(
      invalidItems.flatMap((item) => (item.errors?.length > 0 ? item.errors : [item.label]))
    )}`;
  }
  if (dependentItems.length > 0) {
    // Некорректным проверка могла назвать и зафиксированное действие, которому
    // мешает более позднее: снимается более позднее, а «них» в сообщении нет
    const header = invalidItems.length > 0
      ? 'Отменено вместе с ними — без них это нельзя было построить:'
      : 'Отменено, чтобы остальное строительство стало корректным:';
    message += `\n\n${header}${formatMessageLines(dependentItems.map((item) => item.label))}`;
  }
  if (tailItems.length > 0) {
    message += `\n\nОтменено с конца хода:${formatMessageLines(tailItems.map((item) => item.label))}`;
  }
  message += keptCount > 0
    ? `\n\nОстальные действия строительства (${keptCount}) сохранены.`
    : '\n\nВсе действия строительства этого хода отменены.';
  return message;
};

// Заголовок и строение текста отката с конца — прежние; подписи действий —
// общие с выборочным сообщением
const buildFromEndMessage = (validationResult, items) => {
  let message = buildInvalidPendingBuildingsMessage(validationResult, INVALID_PENDING_BUILDINGS_HEADERS.SERVER);
  message += '\n\nДействия были отменены до последнего корректного состояния поля!\n\nОтмененные действия строительства:';
  // В порядке отмены: с конца хода
  [...items].reverse().forEach((item) => {
    message += `\n• ${item.label}`;
  });
  return message;
};

/**
 * Откат с конца, как кнопка отмены: действия снимаются по одному, пока поле не
 * пройдет проверку конца хода. Так сервер закрывал ход до выборочной отмены;
 * теперь это запасной путь — когда выборочного плана нет (некорректно
 * зафиксированное действие, ход длиннее бюджета) либо он разошелся с
 * редьюсерами. Зависит только от проверки конца хода и редьюсеров отмены,
 * поэтому работает и после сбоя планировщика, и поверх наполовину примененного
 * плана: стор читается заново на каждом шаге.
 */
const rollbackPendingBuildingsFromEnd = (store) => {
  let pendingBuildings = selectPendingBuildings(store.getState());
  let validationResult = validateCurrentPlayerPendingBuildings(store.getState());

  // Редьюсер, не принявший отмену, не должен зациклить откат
  let previousLength;
  while (previousLength !== pendingBuildings.length && !validationResult.isValid && pendingBuildings.length > 0) {
    previousLength = pendingBuildings.length;
    // Тот же экшен отмены, что у кнопки отмены хода: ремонт и постройка
    // откатываются разными редьюсерами
    store.dispatch(createUndoConstructionAction(pendingBuildings[pendingBuildings.length - 1]));
    // Проверка — целиком по состоянию ПОСЛЕ отката: снятое строение больше не
    // «подпирает» соседние (базовый квартал без откаченного центрального)
    const updatedState = store.getState();
    pendingBuildings = selectPendingBuildings(updatedState);
    validationResult = validateCurrentPlayerPendingBuildings(updatedState);
  }
};

const toLogRecord = ({type, hexKey, token, isCommited}) => ({type, hexKey, token, isCommited});

const logFallback = (fallbackReason, originalRecords, error) => {
  const snapshot = originalRecords.map(toLogRecord);
  // Ожидаемые отказы планировщика — предупреждение, остальное — ошибка кода
  const isExpected = Object.values(CONSTRUCTION_ROLLBACK_FALLBACK_REASONS).includes(fallbackReason);
  const log = isExpected ? console.warn : console.error;
  log(`[rollbackInvalidPendingBuildings] откат с конца вместо выборочной отмены: ${fallbackReason}`, snapshot, error || '');
};

/**
 * Ход строительства закрывает не сам игрок (серверный таймер, ушедший игрок,
 * таймер локальной игры), а строения хода размещены некорректно. Игрок, сам
 * завершающий ход, в этом случае получает отказ и исправляет размещение; здесь
 * исправлять некому — некорректное снимается.
 *
 * Снимаются только некорректные действия и те, что без них нельзя было сделать
 * (planConstructionRollback); остальные действия хода остаются. План считается
 * на модели, проверяется пробным прогоном настоящими редьюсерами и только
 * потом применяется к стору — существующими экшенами отмены, от поздних
 * действий к ранним. Нет плана, план разошелся с редьюсерами, расчет упал —
 * ход дожимает откат с конца (rollbackPendingBuildingsFromEnd), как раньше.
 *
 * Не бросает: вызывающий после нее завершает фазу, а из колбэка серверного
 * таймера исключению некуда всплыть — ход остался бы незакрытым.
 *
 * @param {Object} store
 * @param {{limits?: Object}} [options] - limits — бюджет планировщика
 *   (CONSTRUCTION_ROLLBACK_LIMITS), если нужен не обычный
 * @returns {{usedFallback: boolean, fallbackReason: string|null}}
 */
export const rollbackInvalidPendingBuildings = (store, {limits} = {}) => {
  let validationResult = null;
  let originalRecords = [];
  let plan = null;
  let fallbackReason = null;
  let fallbackError = null;

  try {
    const state = store.getState();
    validationResult = validateCurrentPlayerPendingBuildings(state);
    if (validationResult.isValid) {
      return {usedFallback: false, fallbackReason: null};
    }
    originalRecords = selectPendingBuildings(state);

    plan = planConstructionRollback(state, limits && {limits});
    if (plan.fallbackReason) {
      fallbackReason = plan.fallbackReason;
    } else if (!checkConstructionUndoPlanOnCopy(state, plan.removedIndexes)) {
      fallbackReason = APPLY_FALLBACK_REASONS.COPY_MISMATCH;
    } else if (!applyConstructionUndoPlan(store, plan.removedIndexes, originalRecords)) {
      fallbackReason = APPLY_FALLBACK_REASONS.APPLY_MISMATCH;
    } else if (!validateCurrentPlayerPendingBuildings(store.getState()).isValid) {
      fallbackReason = APPLY_FALLBACK_REASONS.STILL_INVALID;
    }
  } catch (error) {
    fallbackReason = APPLY_FALLBACK_REASONS.EXCEPTION;
    fallbackError = error;
  }

  if (fallbackReason) {
    try {
      logFallback(fallbackReason, originalRecords, fallbackError);
      rollbackPendingBuildingsFromEnd(store);
    } catch (error) {
      console.error('[rollbackInvalidPendingBuildings] откат с конца не выполнен', error);
    }
  } else if (plan.keptUnverifiableIndexes.length > 0) {
    // Действие осталось, хотя перед ним что-то снято, а держалось ли оно на
    // снятом, выяснить нельзя: правило клика оно не проходило и в исходном
    // ходе (ход собран в обход клиента либо у Людей сменилась политика)
    console.warn(
      '[rollbackInvalidPendingBuildings] оставлены действия, не проходившие правило клика в исходном ходе',
      plan.keptUnverifiableIndexes.map((index) => toLogRecord(originalRecords[index]))
    );
  }

  // Список красных молотков сам между ходами не сбрасывается, а отмена убирает
  // из него только свою ячейку: строение, ставшее корректным после снятия
  // соседнего, осталось бы красным и на следующем ходу
  try {
    if ((selectInvalidPendingBuildings(store.getState()) || []).length > 0) {
      store.dispatch(setInvalidPendingBuildings([]));
    }
  } catch (error) {
    console.error('[rollbackInvalidPendingBuildings] список некорректных строений не сброшен', error);
  }

  // Проверка упала раньше, чем стало известно, что поле некорректно, —
  // сообщать игроку не о чем
  if (validationResult) {
    try {
      const currentRecords = selectPendingBuildings(store.getState());
      const items = findUndoneIndexes(originalRecords, currentRecords).map((index) => {
        const building = originalRecords[index];
        // Откат с конца: причина по отдельному действию неизвестна
        const reason = fallbackReason ? CONSTRUCTION_ROLLBACK_REASONS.TAIL : plan.reasonByIndex[index];
        return {
          hexKey: building.hexKey,
          token: building.token || null,
          kind: getRecordKind(building),
          label: getRecordLabel(building),
          reason,
          ...(plan?.errorsByIndex[index] && !fallbackReason && {errors: plan.errorsByIndex[index]}),
        };
      });
      const message = fallbackReason
        ? buildFromEndMessage(validationResult, items)
        : buildSelectiveMessage(items, currentRecords.length);
      store.dispatch(sendConstructionValidationMessage(message, {
        usedFallback: Boolean(fallbackReason),
        keptCount: currentRecords.length,
        items,
      }));
    } catch (error) {
      console.error('[rollbackInvalidPendingBuildings] сообщение об отмене не отправлено', error);
    }
  }

  return {usedFallback: Boolean(fallbackReason), fallbackReason};
};
